import { parameterUtils, type ModelData, type ModelParameter } from '@/shared/stores/model-utils'
import {
  frameThickness,
  glassHingeWidth,
  glassHingeHeight,
  glassLockWidth,
  glassLockHeight,
} from '@/shared/config/model-constants'
import type { DimensionHandler } from '@/shared/stores/create-model-store'

function getParam(params: ModelParameter[], id: string) {
  return parameterUtils.findParameter(params, id)
}

function updateParam(model: ModelData, id: string, updates: Partial<ModelParameter>) {
  model.parameters = parameterUtils.updateParameterInArray(model.parameters!, id, updates)
}

/** Template4 Layout: [LeftFrame][LeftPanel][TopRight/Door][FarRight][RightFrame] */
function recalculatePositions(model: ModelData) {
  const params = model.parameters!
  const leftFrame = getParam(params, 'left_frame_profile')
  const topFrame = getParam(params, 'top_frame_profile')
  const leftPanel = getParam(params, 'left_glass_panel')
  const topRightPanel = getParam(params, 'top_right_glass_panel')
  const doorPanel = getParam(params, 'door_glass_panel')
  const farRightPanel = getParam(params, 'far_right_glass_panel')

  if (!leftFrame || !topFrame) return

  let currentX = leftFrame.width
  const topY = topFrame.height

  if (leftPanel) {
    updateParam(model, 'left_glass_panel', {
      position: { x: currentX, y: topY, z: leftPanel.position?.z || 0 },
    })
    currentX += leftPanel.width
  }

  if (topRightPanel) {
    updateParam(model, 'top_right_glass_panel', {
      position: { x: currentX, y: topY, z: topRightPanel.position?.z || 0 },
    })
  }

  if (doorPanel && topRightPanel) {
    updateParam(model, 'door_glass_panel', {
      position: { x: currentX, y: topY + topRightPanel.height, z: doorPanel.position?.z || 0 },
    })
  }

  if (farRightPanel && topRightPanel) {
    currentX += topRightPanel.width
    updateParam(model, 'far_right_glass_panel', {
      position: { x: currentX, y: topY, z: farRightPanel.position?.z || 0 },
    })
  }
}

function updateDoorHardware(model: ModelData, doorHeight: number) {
  const doorPanel = getParam(model.parameters!, 'door_glass_panel')
  if (!doorPanel?.hardware) return

  const updatedHardware = { ...doorPanel.hardware }

  if (updatedHardware.handles) {
    updatedHardware.handles = updatedHardware.handles.map((handle) => ({
      ...handle,
      position: { ...handle.position, y: Math.round(doorHeight * 0.47) },
    }))
  }
  if (updatedHardware.hinges) {
    updatedHardware.hinges = updatedHardware.hinges.map((hinge) => {
      if (hinge.position.y < 0) {
        return { ...hinge, position: { ...hinge.position, y: -glassHingeHeight / 2 } }
      }
      return { ...hinge, position: { ...hinge.position, y: doorHeight - glassHingeHeight } }
    })
  }
  if (updatedHardware.locks) {
    updatedHardware.locks = updatedHardware.locks.map((lock) => ({
      ...lock,
      position: { ...lock.position, y: doorHeight - glassLockHeight },
    }))
  }

  updateParam(model, 'door_glass_panel', { hardware: updatedHardware })
}

const totalWidth: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options

  const currentTotalWidth = model.width as number
  const widthDifference = numValue - currentTotalWidth

  model.width = numValue
  updateParam(model, 'top_frame_profile', { width: numValue })

  if (cascadeUpdates) {
    const halfDifference = widthDifference / 2
    const bottomLeftFrame = getParam(model.parameters!, 'bottom_left_frame_profile')
    const bottomRightFrame = getParam(model.parameters!, 'bottom_right_frame_profile')
    const leftFrame = getParam(model.parameters!, 'left_frame_profile')
    const rightFrame = getParam(model.parameters!, 'right_frame_profile')
    const doorPanel = getParam(model.parameters!, 'door_glass_panel')

    if (bottomLeftFrame && leftFrame) {
      const newLeftSectionWidth = bottomLeftFrame.width + halfDifference
      updateParam(model, 'bottom_left_frame_profile', { width: newLeftSectionWidth })
      const leftPanel = getParam(model.parameters!, 'left_glass_panel')
      if (leftPanel) {
        updateParam(model, 'left_glass_panel', { width: newLeftSectionWidth - leftFrame.width })
      }
    }

    if (bottomRightFrame) {
      const newRightSectionWidth = bottomRightFrame.width + halfDifference
      updateParam(model, 'bottom_right_frame_profile', { width: newRightSectionWidth })
      const farRightPanel = getParam(model.parameters!, 'far_right_glass_panel')
      if (farRightPanel) {
        const newFarRightPanelWidth = farRightPanel.width + halfDifference
        if (newFarRightPanelWidth > 0) {
          updateParam(model, 'far_right_glass_panel', { width: newFarRightPanelWidth })
        }
      }
    }

    if (rightFrame && bottomLeftFrame && doorPanel) {
      const topRightPanel = getParam(model.parameters!, 'top_right_glass_panel')
      const newLeftSectionWidth = bottomLeftFrame.width + halfDifference
      const doorWidth = doorPanel.width
      const topRightPanelWidth = topRightPanel?.width || 0
      const newRightFrameX = newLeftSectionWidth + doorWidth + topRightPanelWidth
      updateParam(model, 'right_frame_profile', {
        position: { ...rightFrame.position, x: newRightFrameX },
      })
      if (bottomRightFrame) {
        const br = getParam(model.parameters!, 'bottom_right_frame_profile')
        if (br) {
          updateParam(model, 'bottom_right_frame_profile', {
            position: { ...br.position, x: newRightFrameX },
          })
        }
      }
    }

    recalculatePositions(model)
  }

  return model
}

const totalHeight: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options

  model.height = numValue
  updateParam(model, 'left_frame_profile', { height: numValue })
  updateParam(model, 'right_frame_profile', { height: numValue })

  if (cascadeUpdates) {
    const topRightPanel = getParam(model.parameters!, 'top_right_glass_panel')
    const topFrame = getParam(model.parameters!, 'top_frame_profile')

    if (topRightPanel && topFrame) {
      const newDoorHeight = numValue - topFrame.height - topRightPanel.height

      if (newDoorHeight > 0) {
        updateParam(model, 'door_glass_panel', { height: newDoorHeight })
        updateDoorHardware(model, newDoorHeight)

        const leftFrame = getParam(model.parameters!, 'left_frame_profile')
        const rightFrame = getParam(model.parameters!, 'right_frame_profile')
        if (leftFrame) {
          updateParam(model, 'left_glass_panel', {
            height: leftFrame.height - frameThickness - frameThickness,
          })
        }
        if (rightFrame) {
          updateParam(model, 'far_right_glass_panel', {
            height: rightFrame.height - frameThickness - frameThickness,
          })
        }

        const bottomLeft = getParam(model.parameters!, 'bottom_left_frame_profile')
        const bottomRight = getParam(model.parameters!, 'bottom_right_frame_profile')
        if (bottomLeft) {
          updateParam(model, 'bottom_left_frame_profile', {
            position: { ...bottomLeft.position, y: numValue - frameThickness },
          })
        }
        if (bottomRight) {
          updateParam(model, 'bottom_right_frame_profile', {
            position: { ...bottomRight.position, y: numValue - frameThickness },
          })
        }
      }
    }
  }

  return model
}

const parameterWidth: DimensionHandler = (model, _initial, numValue, options) => {
  const { positionId, cascadeUpdates = true } = options as { positionId?: string; cascadeUpdates?: boolean }
  if (!positionId) return model

  const currentParameter = getParam(model.parameters!, positionId as string)
  if (!currentParameter) return model

  const currentWidth = currentParameter.width
  const widthDifference = numValue - currentWidth
  updateParam(model, positionId as string, { width: numValue })

  if (cascadeUpdates) {
    if (positionId === 'door_glass_panel' || positionId === 'top_right_glass_panel') {
      const otherPanel = positionId === 'door_glass_panel'
        ? 'top_right_glass_panel'
        : 'door_glass_panel'
      updateParam(model, otherPanel, { width: numValue })
      model.doorConfiguration = {
        ...model.doorConfiguration!,
        totalDoorWidth: numValue,
        individualDoorWidth: [numValue],
      }
      model.width = (model.width as number) + widthDifference
      updateParam(model, 'top_frame_profile', { width: model.width as number })

      const doorPanel = getParam(model.parameters!, 'door_glass_panel')
      if (doorPanel?.hardware) {
        const updatedHardware = { ...doorPanel.hardware }
        if (updatedHardware.handles) {
          updatedHardware.handles = updatedHardware.handles.map((h) => ({
            ...h, position: { ...h.position, x: 50 },
          }))
        }
        if (updatedHardware.hinges) {
          updatedHardware.hinges = updatedHardware.hinges.map((hinge) => {
            if (hinge.positionId === 'door_left_top_hinge_1') {
              return { ...hinge, position: { ...hinge.position, x: -glassHingeWidth / 2 } }
            } else if (hinge.positionId === 'door_left_bottom_hinge_2') {
              return { ...hinge, position: { ...hinge.position, x: 0 } }
            }
            return hinge
          })
        }
        if (updatedHardware.locks) {
          updatedHardware.locks = updatedHardware.locks.map((lock) => ({
            ...lock, position: { ...lock.position, x: numValue - glassLockWidth },
          }))
        }
        updateParam(model, 'door_glass_panel', { hardware: updatedHardware })
      }

      recalculatePositions(model)
    } else if (positionId === 'left_glass_panel') {
      const leftFrame = getParam(model.parameters!, 'left_frame_profile')
      if (leftFrame) {
        updateParam(model, 'bottom_left_frame_profile', { width: numValue + leftFrame.width })
      }
      model.width = (model.width as number) + widthDifference
      updateParam(model, 'top_frame_profile', { width: model.width as number })
      recalculatePositions(model)
    } else if (positionId === 'far_right_glass_panel') {
      const doorPanel = getParam(model.parameters!, 'door_glass_panel')
      const bottomRightWidth = (doorPanel?.width || 900) + numValue + 40
      updateParam(model, 'bottom_right_frame_profile', { width: bottomRightWidth })
      model.width = (model.width as number) + widthDifference
      updateParam(model, 'top_frame_profile', { width: model.width as number })
      recalculatePositions(model)
    }
  }

  return model
}

const topRightHeight: DimensionHandler = (model, _initial, numValue) => {
  const currentTopRight = getParam(model.parameters!, 'top_right_glass_panel')
  const doorPanel = getParam(model.parameters!, 'door_glass_panel')
  const topFrame = getParam(model.parameters!, 'top_frame_profile')

  if (!currentTopRight || !doorPanel || !topFrame) return model

  const newTopRightHeight = numValue - topFrame.height
  const newTransomHeight = numValue

  updateParam(model, 'top_right_glass_panel', {
    height: newTopRightHeight,
    position: { ...currentTopRight.position, y: frameThickness },
  })

  updateParam(model, 'door_glass_panel', {
    position: { ...doorPanel.position, y: newTransomHeight },
  })

  const newTotalHeight = newTransomHeight + doorPanel.height
  model.height = newTotalHeight
  updateParam(model, 'left_frame_profile', { height: newTotalHeight })
  updateParam(model, 'right_frame_profile', { height: newTotalHeight })

  const leftFrame = getParam(model.parameters!, 'left_frame_profile')
  const rightFrame = getParam(model.parameters!, 'right_frame_profile')
  if (leftFrame) {
    updateParam(model, 'left_glass_panel', {
      height: leftFrame.height - frameThickness - frameThickness,
    })
  }
  if (rightFrame) {
    updateParam(model, 'far_right_glass_panel', {
      height: rightFrame.height - frameThickness - frameThickness,
    })
  }

  const bottomLeft = getParam(model.parameters!, 'bottom_left_frame_profile')
  const bottomRight = getParam(model.parameters!, 'bottom_right_frame_profile')
  if (bottomLeft) {
    updateParam(model, 'bottom_left_frame_profile', {
      position: { ...bottomLeft.position, y: newTotalHeight - frameThickness },
    })
  }
  if (bottomRight) {
    updateParam(model, 'bottom_right_frame_profile', {
      position: { ...bottomRight.position, y: newTotalHeight - frameThickness },
    })
  }

  return model
}

const doorPanelHeight: DimensionHandler = (model, _initial, numValue) => {
  const doorPanel = getParam(model.parameters!, 'door_glass_panel')
  const topRightPanel = getParam(model.parameters!, 'top_right_glass_panel')
  const topFrame = getParam(model.parameters!, 'top_frame_profile')

  if (!doorPanel || !topRightPanel || !topFrame) return model

  updateParam(model, 'door_glass_panel', { height: numValue })

  const newTotalHeight = topFrame.height + topRightPanel.height + numValue
  model.height = newTotalHeight
  updateParam(model, 'left_frame_profile', { height: newTotalHeight })
  updateParam(model, 'right_frame_profile', { height: newTotalHeight })

  const leftFrame = getParam(model.parameters!, 'left_frame_profile')
  const rightFrame = getParam(model.parameters!, 'right_frame_profile')
  if (leftFrame) {
    updateParam(model, 'left_glass_panel', {
      height: leftFrame.height - frameThickness - frameThickness,
    })
  }
  if (rightFrame) {
    updateParam(model, 'far_right_glass_panel', {
      height: rightFrame.height - frameThickness - frameThickness,
    })
  }

  const bottomLeft = getParam(model.parameters!, 'bottom_left_frame_profile')
  const bottomRight = getParam(model.parameters!, 'bottom_right_frame_profile')
  if (bottomLeft) {
    updateParam(model, 'bottom_left_frame_profile', {
      position: { ...bottomLeft.position, y: newTotalHeight - frameThickness },
    })
  }
  if (bottomRight) {
    updateParam(model, 'bottom_right_frame_profile', {
      position: { ...bottomRight.position, y: newTotalHeight - frameThickness },
    })
  }

  updateDoorHardware(model, numValue)

  return model
}

const leftFrameWidth: DimensionHandler = (model, _initial, numValue) => {
  const bottomLeft = getParam(model.parameters!, 'bottom_left_frame_profile')
  const leftFrame = getParam(model.parameters!, 'left_frame_profile')

  if (!bottomLeft || !leftFrame) return model

  const widthDifference = numValue - bottomLeft.width

  updateParam(model, 'bottom_left_frame_profile', { width: numValue })
  updateParam(model, 'left_glass_panel', { width: numValue - leftFrame.width })

  model.width = (model.width as number) + widthDifference
  updateParam(model, 'top_frame_profile', { width: model.width as number })

  recalculatePositions(model)
  return model
}

const rightFrameWidth: DimensionHandler = (model, _initial, numValue) => {
  const bottomRight = getParam(model.parameters!, 'bottom_right_frame_profile')
  const rightFrame = getParam(model.parameters!, 'right_frame_profile')

  if (!bottomRight || !rightFrame) return model

  const widthDifference = numValue - bottomRight.width

  updateParam(model, 'bottom_right_frame_profile', { width: numValue })
  const newFarRightPanelWidth = numValue - rightFrame.width
  if (newFarRightPanelWidth > 0) {
    updateParam(model, 'far_right_glass_panel', { width: newFarRightPanelWidth })
  }

  model.width = (model.width as number) + widthDifference
  updateParam(model, 'top_frame_profile', { width: model.width as number })

  const bottomLeft = getParam(model.parameters!, 'bottom_left_frame_profile')
  const doorPanel = getParam(model.parameters!, 'door_glass_panel')
  const topRightPanel = getParam(model.parameters!, 'top_right_glass_panel')
  if (bottomLeft && doorPanel) {
    const leftSectionWidth = bottomLeft.width
    const doorWidth = doorPanel.width
    const topRightPanelWidth = topRightPanel?.width || 0
    const newRightFrameX = leftSectionWidth + doorWidth + topRightPanelWidth
    updateParam(model, 'right_frame_profile', {
      position: { ...rightFrame.position, x: newRightFrameX },
    })
    updateParam(model, 'bottom_right_frame_profile', {
      position: { ...bottomRight.position, x: newRightFrameX },
    })
  }

  recalculatePositions(model)
  return model
}

export const model4DimensionHandlers: Record<string, DimensionHandler> = {
  totalWidth,
  totalHeight,
  parameterWidth,
  topRightHeight,
  doorPanelHeight,
  leftFrameWidth,
  rightFrameWidth,
}
